type BrowserCookieOptions = {
  /**
   * The number of days until the cookie expires.
   */
  expires?: number;
  /**
   * The path for which the cookie is valid.
   */
  path?: string;
  /**
   * The domain for which the cookie is valid.
   */
  domain?: string;
  /**
   * Indicates whether the cookie should only be sent over secure (HTTPS) connections.
   */
  secure?: boolean;
  /**
   * The SameSite attribute of the cookie. Values can be 'Strict', 'Lax', or 'None'.
   */
  sameSite?: "Strict" | "Lax" | "None";
  /**
   * Indicates whether the cookie is accessible only via HTTP and not JavaScript.
   */
  httpOnly?: boolean;
};

/**
 * A utility for working with browser cookies.
 */
export const browserCookie = {
  /**
   * Delete a browser cookie by name.
   * @param {string} name - The name of the cookie to delete.
   */
  deleteCookie(name: string) {
    this.setCookie(name, "", { expires: -1 });
  },

  /**
   * Get the value of a browser cookie by name.
   * @param {string} name - The name of the cookie to retrieve.
   * @returns {string|null} The value of the cookie, or null if the cookie doesn't exist.
   */
  getCookie(name: string) {
    const match = document.cookie.match(new RegExp(`(^| )${name}=([^;]+)`));
    if (match?.[2]) return match[2];
    return null;
  },

  /**
   * Set a browser cookie with the given name, value, and options.
   * @param {string} name - The name of the cookie.
   * @param {string} value - The value to store in the cookie.
   * @param {BrowserCookieOptions} [options] - Options for configuring the cookie.
   */
  setCookie(name: string, value: string, options: BrowserCookieOptions = {}) {
    const { expires, path, domain, secure, sameSite, httpOnly } = options;

    const cookieString = [`${name}=${value}`];

    if (expires) {
      const date = new Date();
      date.setTime(date.getTime() + expires * 24 * 60 * 60 * 1000);
      cookieString.push(`expires=${date.toUTCString()}`);
    }

    if (path) {
      cookieString.push(`path=${path}`);
    }

    if (domain) {
      cookieString.push(`domain=${domain}`);
    }

    if (secure) {
      cookieString.push("secure");
    }

    if (sameSite) {
      cookieString.push(`samesite=${sameSite}`);
    }

    if (httpOnly) {
      cookieString.push("HttpOnly");
    }

    document.cookie = cookieString.join("; ");
  },
};

/**
 * A utility for parsing server-side cookies from a raw cookie string.
 */
export const serverCookie = {
  /**
   * Parse server-side cookies from a raw cookie string.
   * @param {string} rawCookies - The raw cookie string received from the server.
   * @returns {{ [key: string]: string }|null} An object containing parsed cookies, or null if no cookies were provided.
   */
  parseCookies(rawCookies: string) {
    if (rawCookies) {
      const cookieStrings = rawCookies.split(";");

      const cookies: { [key: string]: string } = cookieStrings.reduce((acc, cookieString) => {
        const [key, value] = cookieString.trim().split("=");
        return { ...acc, [key]: decodeURIComponent(value) };
      }, {});

      return cookies;
    }
    return null;
  },
};
